import { useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

import { TelField } from 'app/components/design-system-components/inputs/Tel'
import theme from 'app/match/applicationReportPDF/assets/theme'

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray300};
  border-top: none;
  border-radius: 0 0 ${theme.radii[2]}px ${theme.radii[2]}px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px ${theme.colors.blacks[1]};
`

const DropdownItem = styled.div`
  padding: ${theme.space[4]}px ${theme.space[5]}px;
  cursor: pointer;
  border-bottom: 1px solid ${theme.colors.gray200};

  &:hover {
    background-color: ${theme.colors.gray100};
  }

  &:last-child {
    border-bottom: none;
  }
`

const LoadingItem = styled.div`
  padding: ${theme.space[4]}px ${theme.space[5]}px;
  text-align: center;
  color: ${theme.colors.gray600};
`

const NoResultsItem = styled.div`
  padding: 0 ${theme.space[3]}px ${theme.space[1]}px;
  color: ${theme.colors.gray700};
  font-size: ${theme.fontSizes.pRegular14};
  margin: ${theme.space[3]}px;
`

const ResultsHeader = styled.div`
  padding: ${theme.space[4]}px;
  text-transform: uppercase;
  color: ${theme.colors.gray700};
  font-size: ${theme.fontSizes.pExtraSmall10};
  font-weight: ${theme.fontWeights.bold};
`

export const AutocompleteTelField = ({
  name,
  options = [],
  onChangeText,
  changeHandler,
  isLoading = false,
  placeholder,
  value,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(value || '')
  const [hasSearched, setHasSearched] = useState(false)
  const [isUpdatingFromProps, setIsUpdatingFromProps] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    // Flag that we're updating from props to prevent search triggering
    setIsUpdatingFromProps(true)
    setInputValue(value || '')

    // Reset flag after a short delay to allow the IntlTelInputWrapper to process
    const timer = setTimeout(() => {
      setIsUpdatingFromProps(false)
    }, 0)

    return () => clearTimeout(timer)
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleInputChange = (newValue) => {
    setInputValue(newValue)

    // Don't open dropdown or trigger search if this is a programmatic update from props
    if (isUpdatingFromProps) {
      return
    }

    setIsOpen(true)

    // Track if we've searched (input is >= 2 characters)
    if (newValue.length >= 2) {
      setHasSearched(true)
    } else {
      setHasSearched(false)
    }

    if (onChangeText) {
      onChangeText(newValue)
    }
  }

  const handleOptionSelect = (option) => {
    const selectedValue =
      option.option?.phone ||
      option.option?.mobile ||
      option.optionMeta?.displayText ||
      option
    setInputValue(selectedValue)
    setIsOpen(false)

    if (changeHandler) {
      changeHandler(option)
    }
  }

  const handleInputFocus = () => {
    setIsOpen(true)
  }

  const shouldShowDropdown =
    isOpen &&
    (options.length > 0 || isLoading || (hasSearched && options.length === 0))

  return (
    <AutocompleteContainer ref={containerRef}>
      <TelField
        {...props}
        name={name}
        value={inputValue}
        placeholder={placeholder}
        changeHandler={handleInputChange}
        onFocus={handleInputFocus}
      />

      {shouldShowDropdown && (
        <DropdownContainer>
          <ResultsHeader>RESULTS</ResultsHeader>
          {isLoading ? (
            <LoadingItem>Searching...</LoadingItem>
          ) : options.length > 0 ? (
            <>
              {options.map((option, index) => (
                <DropdownItem
                  key={option.optionMeta?.id || index}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.optionMeta?.displayText ||
                    option.option?.phone ||
                    option.option?.mobile ||
                    option}
                </DropdownItem>
              ))}
            </>
          ) : (
            <NoResultsItem>No results to show</NoResultsItem>
          )}
        </DropdownContainer>
      )}
    </AutocompleteContainer>
  )
}
